import { ACTIVITY_VIEWS, type ActivityView } from '@erp/shared';
import { CalendarDays, ChevronLeft, ChevronRight, List, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ActivityDialog } from '../../components/activities/activity-dialog.tsx';
import { ActivityList } from '../../components/activities/activity-list.tsx';
import { Calendar, rangeOf, type CalendarMode } from '../../components/activities/calendar.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Dropdown } from '../../components/ui/dropdown.tsx';
import { Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import {
  addDays,
  dayLabel,
  monthLabel,
  todayIn,
  useActivities,
  useActivity,
  useActivityTypes,
  useCalendar,
  zonedToIso,
  type ActivityItem,
} from '../../lib/activities-api.ts';
import { errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { useAssignees } from '../../lib/contacts-api.ts';
import { useCan, useSession } from '../../lib/session.ts';

const VIEWS = Object.keys(ACTIVITY_VIEWS) as ActivityView[];
const MODES: [CalendarMode, string][] = [
  ['day', 'Día'],
  ['week', 'Semana'],
  ['month', 'Mes'],
];

/** HU-AGE-02/03: my day as a list, and the calendar (mine or my team's). */
export function AgendaPage() {
  const [params, setParams] = useSearchParams();
  const canEdit = useCan('records.edit');
  const canDelete = useCan('records.delete');
  const [creating, setCreating] = useState(false);
  const calendar = params.get('vista') === 'calendario';
  const openId = params.get('actividad');
  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">Agenda</h1>
        <div className="flex flex-wrap gap-2">
          <div
            className="flex rounded-lg border border-border p-0.5"
            role="group"
            aria-label="Vista"
          >
            <Button
              size="sm"
              variant={calendar ? 'ghost' : 'secondary'}
              aria-pressed={!calendar}
              onClick={() => {
                set({ vista: null });
              }}
            >
              <List /> Lista
            </Button>
            <Button
              size="sm"
              variant={calendar ? 'secondary' : 'ghost'}
              aria-pressed={calendar}
              onClick={() => {
                set({ vista: 'calendario' });
              }}
            >
              <CalendarDays /> Calendario
            </Button>
          </div>
          {canDelete && (
            <Link to="/agenda/papelera" className={buttonVariants({ variant: 'ghost' })}>
              <Trash2 /> Papelera
            </Link>
          )}
          {canEdit && (
            <Button
              variant="brand"
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus /> Nueva actividad
            </Button>
          )}
        </div>
      </div>

      {calendar ? (
        <CalendarView
          params={params}
          set={set}
          onOpen={(a) => {
            set({ actividad: a.id });
          }}
        />
      ) : (
        <ListView params={params} set={set} />
      )}

      {creating && (
        <ActivityDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {openId && (
        <ActivityDetailDialog
          id={openId}
          onClose={() => {
            set({ actividad: null });
          }}
        />
      )}
    </div>
  );
}

function ListView({
  params,
  set,
}: {
  params: URLSearchParams;
  set: (changes: Record<string, string | null>) => void;
}) {
  const { data: session } = useSession();
  const me = session?.membership.id ?? '';
  const assignees = useAssignees();
  const types = useActivityTypes();
  const view = VIEWS.find((v) => v === params.get('filtro')) ?? 'today';
  // Default: mine. "todos": everything the caller can see.
  const who = params.get('responsable') ?? me;
  const page = Number(params.get('pagina') ?? '1') || 1;
  const list = useActivities({
    view,
    assignedMembershipId: who === 'todos' ? undefined : who,
    typeId: params.get('tipo') ?? undefined,
    page,
  });
  const others = (assignees.data ?? []).filter((a) => a.membershipId !== me);
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 50));
  const empty: Record<ActivityView, string> = {
    today: 'Nada para hoy.',
    overdue: 'No hay actividades vencidas.',
    upcoming: 'No hay actividades próximas.',
    done: 'Todavía no hay actividades completadas.',
  };

  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Actividades">
          {VIEWS.map((v) => (
            <Button
              key={v}
              size="sm"
              variant={v === view ? 'primary' : 'secondary'}
              aria-pressed={v === view}
              onClick={() => {
                set({ filtro: v === 'today' ? null : v, pagina: null });
              }}
            >
              {ACTIVITY_VIEWS[v]}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Select
            aria-label="Responsable"
            className="sm:w-48"
            value={who}
            onChange={(e) => {
              set({ responsable: e.target.value === me ? null : e.target.value, pagina: null });
            }}
          >
            <option value={me}>Mis actividades</option>
            {others.length > 0 && <option value="todos">Todas las que puedo ver</option>}
            {others.map((a) => (
              <option key={a.membershipId} value={a.membershipId}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Tipo"
            className="sm:w-40"
            value={params.get('tipo') ?? ''}
            onChange={(e) => {
              set({ tipo: e.target.value, pagina: null });
            }}
          >
            <option value="">Todos los tipos</option>
            {(types.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {list.isPending ? (
        <Loading />
      ) : list.error ? (
        <Alert tone="danger">{errorMessage(list.error)}</Alert>
      ) : (
        <ActivityList items={list.data.items} empty={empty[view]} showAssignee={who !== me} />
      )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => {
              set({ pagina: String(page - 1) });
            }}
          >
            Anterior
          </Button>
          <span>
            {page} de {pages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= pages}
            onClick={() => {
              set({ pagina: String(page + 1) });
            }}
          >
            Siguiente
          </Button>
        </div>
      )}
    </Card>
  );
}

function CalendarView({
  params,
  set,
  onOpen,
}: {
  params: URLSearchParams;
  set: (changes: Record<string, string | null>) => void;
  onOpen: (activity: ActivityItem) => void;
}) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone ?? 'America/El_Salvador';
  const me = session?.membership.id ?? '';
  const assignees = useAssignees();
  const narrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
  const mode = MODES.find(([m]) => m === params.get('modo'))?.[0] ?? (narrow ? 'day' : 'week');
  const anchor = /^\d{4}-\d{2}-\d{2}$/.test(params.get('fecha') ?? '')
    ? (params.get('fecha') ?? '')
    : todayIn(tz);
  const people = (params.get('personas') ?? '').split(',').filter(Boolean);
  const members = people.length > 0 ? people : [me];
  const { from, days } = rangeOf(mode, anchor);
  const data = useCalendar(
    zonedToIso(from, '00:00', tz),
    zonedToIso(addDays(from, days), '00:00', tz),
    members,
  );
  const names = new Map<string, string>([
    [me, 'Yo'],
    ...(assignees.data ?? []).map((a) => [a.membershipId, a.name] as [string, string]),
  ]);
  const step = mode === 'day' ? 1 : mode === 'week' ? 7 : 0;
  const move = (direction: number) => {
    const next =
      mode === 'month' ? shiftMonth(anchor, direction) : addDays(anchor, step * direction);
    set({ fecha: next });
  };
  const title =
    mode === 'day'
      ? dayLabel(anchor, true)
      : mode === 'week'
        ? `${dayLabel(from, true)} – ${dayLabel(addDays(from, 6), true)}`
        : monthLabel(anchor);
  const others = (assignees.data ?? []).filter((a) => a.membershipId !== me);
  const overlaps = (data.data ?? []).filter((i) => i.overlap).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              set({ fecha: null });
            }}
          >
            Hoy
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Anterior"
            onClick={() => {
              move(-1);
            }}
          >
            <ChevronLeft />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Siguiente"
            onClick={() => {
              move(1);
            }}
          >
            <ChevronRight />
          </Button>
          <h2 className="font-semibold first-letter:uppercase">{title}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="flex rounded-lg border border-border p-0.5"
            role="group"
            aria-label="Periodo"
          >
            {MODES.map(([m, label]) => (
              <Button
                key={m}
                size="sm"
                variant={m === mode ? 'secondary' : 'ghost'}
                aria-pressed={m === mode}
                onClick={() => {
                  set({ modo: m });
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          {others.length > 0 && (
            <Dropdown
              label={
                <>
                  <Users className="size-4" aria-hidden="true" /> Personas ({members.length})
                </>
              }
            >
              <fieldset className="max-h-72 w-64 space-y-1 overflow-y-auto">
                <legend className="sr-only">Calendarios que se muestran</legend>
                {[{ membershipId: me, name: 'Yo' }, ...others].map((a) => (
                  <label key={a.membershipId} className="flex min-h-9 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4"
                      checked={members.includes(a.membershipId)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...members, a.membershipId]
                          : members.filter((m) => m !== a.membershipId);
                        set({
                          personas:
                            next.length === 1 && next[0] === me
                              ? null
                              : next.slice(0, 20).join(','),
                        });
                      }}
                    />
                    {a.name}
                  </label>
                ))}
              </fieldset>
            </Dropdown>
          )}
        </div>
      </div>
      {overlaps > 0 && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span aria-hidden="true" className="inline-block size-3 rounded-sm ring-2 ring-warning" />
          Los bloques con borde amarillo se cruzan con otra actividad de la misma persona.
        </p>
      )}
      {data.error ? (
        <Alert tone="danger">{errorMessage(data.error)}</Alert>
      ) : data.isPending ? (
        <Loading />
      ) : (
        <div className={cn(data.isFetching && 'opacity-70')}>
          <Calendar
            mode={mode}
            anchor={anchor}
            items={data.data}
            tz={tz}
            members={members.map((m) => ({ membershipId: m, name: names.get(m) ?? '—' }))}
            onOpen={onOpen}
            onPickDay={(date) => {
              set({ modo: 'day', fecha: date });
            }}
          />
        </div>
      )}
    </div>
  );
}

function shiftMonth(date: string, direction: number): string {
  const [y = 0, m = 1] = date.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + direction, 1));
  return d.toISOString().slice(0, 10);
}

function ActivityDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const activity = useActivity(id);
  return (
    <Dialog open onClose={onClose} title="Actividad">
      {activity.isPending ? (
        <Loading />
      ) : activity.error ? (
        <Alert tone="danger">
          {(activity.error as { status?: number }).status === 404
            ? 'No encontramos esta actividad o ya no está en tu agenda.'
            : errorMessage(activity.error)}
        </Alert>
      ) : (
        <ActivityList items={[activity.data]} empty="" showAssignee />
      )}
    </Dialog>
  );
}
